<route lang="yaml">
meta:
  title: 主页
  icon: ant-design:home-twotone
</route>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import apiFontPackage from '@/api/modules/config/font-package'
import apiCopybook from '@/api/modules/copybook'
import apiCopybookTemplate from '@/api/modules/copybook-template'
import apiUser from '@/api/modules/user'
import { useUserStore } from '@/store/modules/user'
import useAuth from '@/utils/composables/useAuth'

const router = useRouter()
const userStore = useUserStore()
const { auth } = useAuth()

// 用户信息
const userName = computed(() => userStore.username || userStore.account || '访客')

// 当前日期
const currentDate = computed(() => {
  return new Date().toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long',
  })
})

type StatValue = number | null

type ModuleStatKey
  = | 'copybook'
    | 'template'
    | 'fontPackage'
    | 'user'

const moduleStats = ref<Record<ModuleStatKey, StatValue>>({
  copybook: null,
  template: null,
  fontPackage: null,
  user: null,
})

const loadingStats = ref(false)

function navigateTo(path: string) {
  router.push(path)
}

function formatStat(value: StatValue) {
  if (value === null) {
    return '—'
  }
  return value.toLocaleString('zh-CN')
}

function pickTotal(payload: any): number {
  const candidates = [
    payload?.data?.total,
    payload?.data?.meta?.total,
    payload?.total,
    payload?.meta?.total,
  ]
  for (const candidate of candidates) {
    const numeric = typeof candidate === 'number' ? candidate : Number(candidate)
    if (Number.isFinite(numeric)) {
      return numeric
    }
  }
  return 0
}

async function safeLoadTotal(loader: () => Promise<any>): Promise<StatValue> {
  try {
    const res = await loader()
    return pickTotal(res)
  }
  catch {
    return null
  }
}

const moduleCards = computed(() => {
  const cards: Array<{
    key: ModuleStatKey
    title: string
    icon: string
    path: string
    auth: string
    iconClass: string
  }> = [
    { key: 'copybook', title: '字帖', icon: 'i-ant-design:file-text-twotone', path: '/copybook', auth: 'copybook:list', iconClass: 'stat-icon-blue' },
    { key: 'template', title: '字帖模板', icon: 'i-ant-design:appstore-twotone', path: '/copybook-template', auth: 'copybook-template:list', iconClass: 'stat-icon-purple' },
    { key: 'fontPackage', title: '字体包', icon: 'i-ant-design:font-size-outlined', path: '/config/font-package', auth: 'config:font-package:list', iconClass: 'stat-icon-green' },
    { key: 'user', title: '员工', icon: 'i-ant-design:user-outlined', path: '/organization/user', auth: 'organization:user:list', iconClass: 'stat-icon-yellow' },
  ]
  return cards.filter(card => auth(card.auth))
})

async function fetchModuleStats() {
  loadingStats.value = true
  try {
    const tasks: Array<{
      key: ModuleStatKey
      auth: string
      loader: () => Promise<any>
    }> = [
      { key: 'copybook', auth: 'copybook:list', loader: () => apiCopybook.getCopybookList({ page: 1, limit: 1 }) },
      { key: 'template', auth: 'copybook-template:list', loader: () => apiCopybookTemplate.getCopybookTemplateList({ page: 1, limit: 1 }) },
      { key: 'fontPackage', auth: 'config:font-package:list', loader: () => apiFontPackage.getFontPackageList({ page: 1, limit: 1 }) },
      { key: 'user', auth: 'organization:user:list', loader: () => apiUser.getUserList({ page: 1, limit: 1 }) },
    ]

    const results = await Promise.all(
      tasks.map(task => auth(task.auth) ? safeLoadTotal(task.loader) : Promise.resolve(null)),
    )
    const next: Record<ModuleStatKey, StatValue> = { ...moduleStats.value }
    tasks.forEach((task, index) => {
      next[task.key] = results[index]
    })
    moduleStats.value = next
  }
  finally {
    loadingStats.value = false
  }
}

onMounted(() => {
  fetchModuleStats()
})
</script>

<template>
  <div class="dashboard-container">
    <!-- 欢迎区域 -->
    <div class="welcome-section">
      <div class="welcome-header">
        <div class="welcome-text">
          <h1 class="welcome-title">
            欢迎回来，{{ userName }}
          </h1>
          <p class="welcome-subtitle">
            今天是 {{ currentDate }}，祝您工作愉快！
          </p>
        </div>
        <div class="welcome-actions">
          <ElButton size="small" plain type="danger" @click="userStore.logout()">
            <FaIcon name="i-ep:switch-button" class="mr-1" />
            退出
          </ElButton>
        </div>
      </div>
    </div>

    <!-- 模块概览 -->
    <div class="stats-section">
      <div class="stats-grid">
        <ElCard
          v-for="card in moduleCards"
          :key="card.key"
          class="stat-card stat-card-clickable"
          shadow="hover"
          :body-style="{ padding: '16px' }"
          @click="navigateTo(card.path)"
        >
          <div class="stat-content">
            <div class="stat-icon" :class="card.iconClass">
              <FaIcon :name="card.icon" />
            </div>
            <div class="stat-info">
              <p class="stat-label">
                {{ card.title }}
              </p>
              <p class="stat-value">
                {{ formatStat(moduleStats[card.key]) }}
              </p>
            </div>
          </div>
          <div class="stat-trend">
            <div class="trend-item">
              <span class="trend-text">{{ loadingStats ? '加载中…' : '进入模块' }}</span>
              <FaIcon name="i-ep:arrow-right" class="ml-1" />
            </div>
          </div>
        </ElCard>
      </div>
    </div>
  </div>
</template>

<style scoped>
.dashboard-container {
  min-height: 100vh;
  padding: 20px;
  background-color: #f5f5f5;
}

.welcome-section {
  margin-bottom: 24px;
}

.welcome-header {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  justify-content: space-between;
}

.welcome-text {
  min-width: 0;
}

.welcome-actions {
  flex: 0 0 auto;
}

.welcome-title {
  margin: 0 0 8px;
  font-size: 28px;
  font-weight: bold;
  color: #333;
}

.welcome-subtitle {
  margin: 0;
  font-size: 14px;
  color: #666;
}

.stats-section {
  margin-bottom: 32px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
}

.stat-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.stat-card-clickable {
  cursor: pointer;
}

.stat-content {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.stat-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin-right: 16px;
  font-size: 20px;
  border-radius: 8px;
}

.stat-icon-blue {
  color: #2563eb;
  background-color: #dbeafe;
}

.stat-icon-green {
  color: #16a34a;
  background-color: #dcfce7;
}

.stat-icon-purple {
  color: #9333ea;
  background-color: #f3e8ff;
}

.stat-icon-yellow {
  color: #d97706;
  background-color: #fef3c7;
}

.stat-info {
  flex: 1;
}

.stat-label {
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
}

.stat-value {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #111827;
}

.stat-value-success {
  color: #16a34a;
}

.stat-trend {
  margin-top: 16px;
}

.trend-item {
  display: flex;
  align-items: center;
  font-size: 14px;
}

.trend-up {
  display: flex;
  align-items: center;
  margin-right: 8px;
  color: #16a34a;
}

.trend-text {
  color: #6b7280;
}

.trend-value {
  margin-left: 4px;
  font-weight: 500;
  color: #111827;
}

@media (width <= 768px) {
  .dashboard-container {
    padding: 16px;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .welcome-title {
    font-size: 24px;
  }
}
</style>
